/**
 * Codec Groups Page
 * 
 * Manage SIP codec groups using Sip.CodecGroupInfo from @sbclite/core.
 * Refactored to use common components for consistent UI patterns.
 */

import React, { useState } from 'react';
import { 
  Music, 
  Plus, 
  CheckCircle, 
  Copy, 
  Settings,
  Info,
  XCircle,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { Sip } from '@sbclite/core';
import { SBC_SUPPORTED_CODECS } from '@sbclite/core/sip/constants';
import { CodecGroupEditor } from '../../components/codec/CodecGroupEditor';
import { getCodecTypeIcon } from '../../components/codec/codecOptions';

// Common components
import {
  PageHeader,
  PageContainer,
  StatsGrid,
  StatCard,
  FilterBar,
  ItemCard,
  ItemCardIcon,
  ItemActions,
  EmptyState,
  LoadingState,
  ErrorState,
  DeleteConfirmModal,
  TabbedDetailsModal,
  TabContent,
  DetailSection,
  QuickStatsGrid,
  QuickStatCard,
  type Tab,
  type FilterOption,
} from '../../components/common';
import { useViewMode } from '../../hooks/useViewMode';

// Use the CodecGroupInfo type from core
type CodecGroupInfo = Sip.CodecGroupInfo;

// ========================================
// HELPER FUNCTIONS
// ========================================

function countCodecsByType(codecs: CodecGroupInfo['codecs']): { audio: number; video: number; fax: number } {
  if (!codecs) return { audio: 0, video: 0, fax: 0 };
  return codecs.reduce((acc, codec) => {
    acc[codec.codecType]++;
    return acc;
  }, { audio: 0, video: 0, fax: 0 });
}

// ========================================
// CONSTANTS
// ========================================

type FilterType = 'all' | 'enabled' | 'disabled';

const FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Groups' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

const DETAILS_TABS: Tab[] = [
  { id: 'overview', label: 'Overview', icon: Info },
  { id: 'codecs', label: 'Codecs', icon: Music },
  { id: 'settings', label: 'Settings', icon: Settings },
];

// ========================================
// MAIN COMPONENT
// ========================================

const CodecGroupsPage: React.FC = () => {
  const queryClient = useQueryClient();
  
  // State management - use user preference for default view mode
  const [viewMode, setViewMode] = useViewMode();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCodecGroup, setSelectedCodecGroup] = useState<CodecGroupInfo | null>(null);
  const [codecGroupToDelete, setCodecGroupToDelete] = useState<CodecGroupInfo | null>(null);
  const [editingCodecGroup, setEditingCodecGroup] = useState<CodecGroupInfo | null>(null);

  // API queries
  const codecGroupsQuery = useQuery({
    queryKey: ['codec-groups'],
    queryFn: async () => {
      const response = await api.get<{ 
        success: boolean; 
        data: CodecGroupInfo[];
      }>('/api/codec-groups');
      return response.data.success ? response.data.data : [];
    }
  });
  
  const codecGroups = codecGroupsQuery.data || [];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (groupId: string) => {
      await api.delete(`/api/codec-groups/${groupId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['codec-groups'] });
      setShowDeleteModal(false);
      setCodecGroupToDelete(null);
    },
    onError: (error: Error) => {
      console.error('Failed to delete codec group:', error);
      const candidate = error as Error & {
        response?: {
          data?: {
            error?: string;
          };
        };
      };
      const message = candidate.response?.data?.error ?? error.message ?? 'Failed to delete codec group. Please try again.';
      alert(message);
    }
  });

  // Utility functions
  const getStatusColor = (enabled: boolean) => {
    return enabled 
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Filter and search logic
  const filteredCodecGroups = codecGroups.filter(group => {
    let matchesType = false;
    if (filterType === 'all') {
      matchesType = true;
    } else if (filterType === 'enabled') {
      matchesType = group.enabled === true;
    } else {
      matchesType = group.enabled === false;
    }

    const matchesSearch = searchQuery === '' || 
      group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (group.description && group.description.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesType && matchesSearch;
  });

  const sortedCodecGroups = [...filteredCodecGroups].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  // Calculate statistics
  const stats = {
    total: codecGroups.length,
    enabled: codecGroups.filter(group => group.enabled === true).length,
    disabled: codecGroups.filter(group => group.enabled === false).length,
  };

  // Loading state
  if (codecGroupsQuery.isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (codecGroupsQuery.error) {
    return (
      <ErrorState
        title="Failed to load codec groups"
        message={codecGroupsQuery.error instanceof Error ? codecGroupsQuery.error.message : 'Unknown error'}
        onRetry={() => codecGroupsQuery.refetch()}
      />
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <PageHeader
        title="Codec Groups"
        description="Manage SIP codec configurations for media processing"
        onRefresh={() => codecGroupsQuery.refetch()}
        isRefreshing={codecGroupsQuery.isFetching}
        primaryAction={{
          label: 'Create Codec Group',
          icon: Plus,
          onClick: () => {
            setEditingCodecGroup(null);
            setShowCreateModal(true);
          },
        }}
      />

      {/* Statistics Cards */}
      <StatsGrid>
        <StatCard
          label="Total Groups"
          value={stats.total}
          icon={Music}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          label="Enabled"
          value={stats.enabled}
          icon={CheckCircle}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
        <StatCard
          label="Disabled"
          value={stats.disabled}
          icon={XCircle}
          iconBgColor="bg-gray-100"
          iconColor="text-gray-600"
          valueColor="text-gray-600"
        />
      </StatsGrid>

      {/* Filters and Search */}
      <FilterBar
        searchPlaceholder="Search codec groups..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filterOptions={FILTER_OPTIONS}
        filterValue={filterType}
        onFilterChange={(value) => setFilterType(value as FilterType)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Codec Groups Display */}
      {sortedCodecGroups.length === 0 ? (
        <EmptyState
          icon={Music}
          title="No codec groups configured"
          description={`If no codec groups are configured, peers still work and use system-installed codecs: ${SBC_SUPPORTED_CODECS.join(', ')}`}
          isFiltered={searchQuery !== '' || filterType !== 'all'}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedCodecGroups.map((codecGroup) => {
            const codecCounts = countCodecsByType(codecGroup.codecs);
            const totalCodecs = codecGroup.codecs?.length || 0;

            return (
              <ItemCard
                key={codecGroup.id}
                icon={
                  <ItemCardIcon 
                    icon={<Music className="w-4 h-4" />} 
                    bgColor="bg-gray-100" 
                  />
                }
                title={codecGroup.name}
                subtitle={codecGroup.description || 'Codec group'}
                status={
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(codecGroup.enabled)}`}>
                    {codecGroup.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                }
                details={[
                  { label: 'Total Codecs', value: totalCodecs },
                  { label: 'Audio / Video / Fax', value: `${codecCounts.audio} / ${codecCounts.video} / ${codecCounts.fax}` },
                  ...(codecGroup.isDefault ? [{ label: 'Default', value: <span className="text-green-600 font-medium">Yes</span> }] : []),
                ]}
                tags={
                  codecGroup.codecs && codecGroup.codecs.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {codecGroup.codecs.slice(0, 5).map((codec, index) => (
                        <span 
                          key={codec.codecId || index}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            codec.enabled ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-500'
                          }`}
                        >
                          {codec.codecName}
                        </span>
                      ))}
                      {codecGroup.codecs.length > 5 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          +{codecGroup.codecs.length - 5} more
                        </span>
                      )}
                    </div>
                  )
                }
                actions={
                  <ItemActions
                    variant="card"
                    actions={[
                      {
                        kind: 'view',
                        onClick: () => {
                          setSelectedCodecGroup(codecGroup);
                          setShowDetailsModal(true);
                        },
                      },
                      {
                        kind: 'edit',
                        onClick: () => {
                          setEditingCodecGroup(codecGroup);
                          setShowCreateModal(true);
                        },
                      },
                      {
                        kind: 'delete',
                        onClick: () => {
                          setCodecGroupToDelete(codecGroup);
                          setShowDeleteModal(true);
                        },
                      },
                    ]}
                  />
                }
              />
            );
          })}
        </div>
      ) : (
        // List View (Table)
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Codecs</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedCodecGroups.map((codecGroup) => {
                const codecCounts = countCodecsByType(codecGroup.codecs);
                const totalCodecs = codecGroup.codecs?.length || 0;

                return (
                  <tr key={codecGroup.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">{codecGroup.name}</div>
                        {codecGroup.description && (
                          <div className="text-xs text-gray-500">{codecGroup.description}</div>
                        )}
                        {codecGroup.isDefault && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1 w-fit">
                            Default
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col text-sm">
                        <div className="text-gray-900">{totalCodecs} total</div>
                        <div className="text-gray-500 text-xs">
                          {codecCounts.audio}A / {codecCounts.video}V / {codecCounts.fax}F
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(codecGroup.enabled)}`}>
                        {codecGroup.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <ItemActions
                        variant="table"
                        actions={[
                          {
                            kind: 'view',
                            onClick: () => {
                              setSelectedCodecGroup(codecGroup);
                              setShowDetailsModal(true);
                            },
                          },
                          {
                            kind: 'edit',
                            onClick: () => {
                              setEditingCodecGroup(codecGroup);
                              setShowCreateModal(true);
                            },
                          },
                          {
                            kind: 'delete',
                            onClick: () => {
                              setCodecGroupToDelete(codecGroup);
                              setShowDeleteModal(true);
                            },
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Codec Group Editor (tabbed modal) */}
      <CodecGroupEditor
        isOpen={showCreateModal}
        editingGroup={editingCodecGroup}
        onClose={() => {
          setShowCreateModal(false);
          setEditingCodecGroup(null);
        }}
        onSuccess={() => {
          setShowCreateModal(false);
          setEditingCodecGroup(null);
        }}
      />

      {/* Codec Group Details Modal */}
      {showDetailsModal && selectedCodecGroup && (
        <CodecGroupDetailsModal
          codecGroup={selectedCodecGroup}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedCodecGroup(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {codecGroupToDelete && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setCodecGroupToDelete(null);
          }}
          onConfirm={() => deleteMutation.mutate(codecGroupToDelete.id)}
          title="Delete Codec Group"
          entityType="codec group"
          entityName={codecGroupToDelete.name}
          additionalInfo={
            <>
              <p className="text-sm text-gray-600 mb-1 mt-2">Codecs:</p>
              <p className="text-sm text-gray-700">
                {codecGroupToDelete.codecs?.length || 0} codec(s) configured
              </p>
            </>
          }
          isDeleting={deleteMutation.isPending}
        />
      )}
    </PageContainer>
  );
};

// ========================================
// CODEC GROUP DETAILS MODAL
// ========================================

interface CodecGroupDetailsModalProps {
  codecGroup: CodecGroupInfo;
  onClose: () => void;
}

const CodecGroupDetailsModal: React.FC<CodecGroupDetailsModalProps> = ({
  codecGroup,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<string>('overview');

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const codecCounts = countCodecsByType(codecGroup.codecs);

  return (
    <TabbedDetailsModal
      isOpen={true}
      onClose={onClose}
      title={codecGroup.name}
      subtitle="Codec Group Details"
      tabs={DETAILS_TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      {activeTab === 'overview' && (
        <TabContent>
          {/* Quick Stats */}
          <QuickStatsGrid>
            <QuickStatCard
              icon={<div className="p-2 bg-blue-100 rounded-lg"><Music className="w-5 h-5 text-blue-600" /></div>}
              label="Total Codecs"
              value={codecGroup.codecs?.length || 0}
            />
            <QuickStatCard
              icon={
                <div className={`p-2 rounded-lg ${codecGroup.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  {codecGroup.enabled ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-gray-600" />
                  )}
                </div>
              }
              label="Status"
              value={codecGroup.enabled ? 'Enabled' : 'Disabled'}
            />
            <QuickStatCard
              icon={<div className="p-2 bg-purple-100 rounded-lg"><Settings className="w-5 h-5 text-purple-600" /></div>}
              label="Default Group"
              value={codecGroup.isDefault ? 'Yes' : 'No'}
            />
          </QuickStatsGrid>

          {/* Codec Distribution */}
          <DetailSection title="Codec Distribution">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{codecCounts.audio}</p>
                  <p className="text-sm text-gray-600">Audio</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{codecCounts.video}</p>
                  <p className="text-sm text-gray-600">Video</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{codecCounts.fax}</p>
                  <p className="text-sm text-gray-600">Fax</p>
                </div>
              </div>
            </div>
          </DetailSection>

          {/* Details */}
          <DetailSection title="Details">
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Group ID</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono">{codecGroup.id}</span>
                  <button
                    onClick={() => copyToClipboard(codecGroup.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {codecGroup.description && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Description</span>
                  <span className="text-sm text-gray-900">{codecGroup.description}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Default Group</span>
                <span className="text-sm text-gray-900">{codecGroup.isDefault ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Created</span>
                <span className="text-sm text-gray-900">{new Date(codecGroup.createdAt).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Updated</span>
                <span className="text-sm text-gray-900">{new Date(codecGroup.updatedAt).toLocaleString()}</span>
              </div>
            </div>
          </DetailSection>
        </TabContent>
      )}

      {activeTab === 'codecs' && (
        <TabContent>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Configured Codecs</h3>
          
          {codecGroup.codecs && codecGroup.codecs.length > 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Codec</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {codecGroup.codecs.map((codec, index) => {
                    const CodecIcon = getCodecTypeIcon(codec.codecType);
                    return (
                    <tr key={codec.codecId || index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <CodecIcon className="w-4 h-4" />
                          <span className="font-medium text-gray-900">{codec.codecName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          codec.codecType === 'audio' ? 'bg-blue-100 text-blue-800' :
                          codec.codecType === 'video' ? 'bg-green-100 text-green-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {codec.codecType}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {codec.priority}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          codec.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {codec.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Music className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No codecs configured in this group</p>
            </div>
          )}
        </TabContent>
      )}

      {activeTab === 'settings' && (
        <TabContent>
          <DetailSection title="Group Settings">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">DTMF Mode</span>
                <span className="text-sm text-gray-900 uppercase">{codecGroup.dtmfMode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Packetization (ptime)</span>
                <span className="text-sm text-gray-900">{codecGroup.ptime ? `${codecGroup.ptime} ms` : 'Not set'}</span>
              </div>
            </div>
          </DetailSection>
        </TabContent>
      )}
    </TabbedDetailsModal>
  );
};

export default CodecGroupsPage;
